import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { MeshDistortMaterial, Sparkles } from "@react-three/drei";
import * as THREE from "three";

export const LEVEL_COLOR = { secure: "#00E5FF", watch: "#FFB800", threat: "#FF003C" };
const SPEED = { idle: 0.35, listening: 1.2, thinking: 2.6, speaking: 1.6 };

const Core = ({ color, mode, levelRef }) => {
  const core = useRef(), shell = useRef(), ring1 = useRef(), ring2 = useRef(), mat = useRef(), glow = useRef();
  const c = useMemo(() => new THREE.Color(color), [color]);
  useFrame((st, dt) => {
    const lv = Math.min(1, (levelRef?.current || 0) * 3);
    const sp = SPEED[mode] || SPEED.idle;
    const pulse = mode === "idle" ? 0.04 * Math.sin(st.clock.elapsedTime * 1.6) : lv * 0.28 + (mode === "thinking" ? 0.06 * Math.sin(st.clock.elapsedTime * 9) : 0);
    core.current.scale.setScalar(1 + pulse);
    shell.current.rotation.y += dt * sp * 0.6;
    shell.current.rotation.x += dt * sp * 0.25;
    ring1.current.rotation.z += dt * sp;
    ring2.current.rotation.x -= dt * sp * 0.8;
    mat.current.distort = 0.25 + lv * 0.45 + (mode === "thinking" ? 0.2 : 0);
    mat.current.speed = 1.5 + sp * 2;
    mat.current.emissiveIntensity = 0.28 + lv * 1.6 + (mode !== "idle" ? 0.25 : 0);
    glow.current.scale.setScalar(1.9 + lv * 0.8 + pulse * 2);
    glow.current.material.opacity = 0.06 + lv * 0.22 + (mode !== "idle" ? 0.05 : 0);
  });
  return (
    <group>
      <mesh ref={glow}><sphereGeometry args={[1, 32, 32]} /><meshBasicMaterial color={c} transparent opacity={0.2} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.BackSide} /></mesh>
      <mesh ref={core}>
        <icosahedronGeometry args={[1, 24]} />
        <MeshDistortMaterial ref={mat} color={c} emissive={c} emissiveIntensity={0.3} roughness={0.08} metalness={0.95} distort={0.3} speed={2} />
      </mesh>
      <mesh ref={shell}><icosahedronGeometry args={[1.45, 1]} /><meshBasicMaterial color={c} wireframe transparent opacity={0.35} /></mesh>
      <mesh ref={ring1} rotation={[Math.PI / 2.4, 0, 0]}><torusGeometry args={[1.85, 0.015, 8, 120]} /><meshBasicMaterial color={c} transparent opacity={0.8} /></mesh>
      <mesh ref={ring2} rotation={[0, Math.PI / 3, 0]}><torusGeometry args={[2.05, 0.01, 8, 120]} /><meshBasicMaterial color={c} transparent opacity={0.5} /></mesh>
      <Sparkles count={40} scale={4.6} size={2.2} speed={mode === "idle" ? 0.3 : 1.2} color={color} />
    </group>
  );
};

export const GuardOrb3D = ({ level = "secure", mode = "idle", levelRef, className = "", testid = "guard-orb-canvas" }) => (
  <div className={className} data-testid={testid}>
    <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 1.5]} gl={{ antialias: true, alpha: true }}>
      <ambientLight intensity={0.4} />
      <pointLight position={[3, 3, 4]} intensity={60} color="#FFFFFF" />
      <pointLight position={[0, 0, 5]} intensity={20} color={LEVEL_COLOR[level]} />
      <pointLight position={[-3, -2, 2]} intensity={12} color="#7000FF" />
      <Core color={LEVEL_COLOR[level] || LEVEL_COLOR.secure} mode={mode} levelRef={levelRef} />
    </Canvas>
  </div>
);
